<template>
  <div
    ref="popover"
    class="ds-popover"
    @click="actionClickHandler"
    @keydown.space.prevent="actionClickHandler"
  >
    <slot name="action" />

    <transition :name="noAnimation ? '' : 'fade'">
      <div
        v-if="val"
        ref="popoverContent"
        class="ds-popover-content shadow-elevation-large"
        :style="style"
        :class="classes"
        @keydown.space.stop
      >
        <slot />
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'Popover',

  emits: ['input', 'update:modelValue'],

  props: {
    position: {
      type: String,
      default: 'bottom',
    },
    hideOnMobile: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    noPadding: {
      type: Boolean,
      default: false,
    },
    contentStyle: {
      type: Object,
      default: null,
    },
    customActionHandler: {
      type: Boolean,
      default: false,
    },
    blockClosingPopoverHandler: {
      type: Function,
      default: null,
    },
    noAnimation: {
      type: Boolean,
      default: false,
    },
    closeOnScroll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      val: this.modelValue, // modelValue comes as a prop, val is data.
      overflowStyle: null, // Styles to fix the Popover overlow
    };
  },
  computed: {
    style() {
      return {
        ...this.overflowStyle,
        ...this.contentStyle,
      };
    },
    classes() {
      let result = `ds-popover-content--${this.position}`;
      if (this.hideOnMobile) {
        result += ' ds-popover-content--desktop';
      }
      if (this.noPadding) {
        result += ' ds-popover-content--no-padding';
      }
      return result;
    },
  },
  watch: {
    val(isOpen) {
      this.$emit('input', isOpen);
      this.$emit('update:modelValue', isOpen);
      if (isOpen) {
        document.addEventListener('mousedown', this.clickOutsideHandler);
        if (this.closeOnScroll) {
          window.addEventListener('scroll', this.scrollCloseHandler, { passive: true });
        }
        this.overflowStyle = null;
        this.$nextTick(this.fixScreenOverflow);
      } else {
        document.removeEventListener('mousedown', this.clickOutsideHandler);
        window.removeEventListener('scroll', this.scrollCloseHandler);
      }
    },
    modelValue(value) {
      this.val = value;
    },
    closeOnScroll(enabled) {
      if (!this.val) {
        return;
      }
      if (enabled) {
        window.addEventListener('scroll', this.scrollCloseHandler, { passive: true });
      } else {
        window.removeEventListener('scroll', this.scrollCloseHandler);
      }
    },
  },
  methods: {
    actionClickHandler(event) {
      if (this.customActionHandler) {
        return;
      }
      if (this.$refs.popoverContent && this.$refs.popoverContent.contains(event.target)) {
        return;
      }
      this.val = !this.val;
    },
    scrollCloseHandler() {
      if (this.closeOnScroll) {
        this.val = false;
      }
    },
    clickOutsideHandler(event) {
      if (typeof this.blockClosingPopoverHandler === 'function' && this.blockClosingPopoverHandler(event)) {
        return;
      }

      if (this.$refs.popover && !this.$refs.popover.contains(event.target) && this.val) {
        this.val = false;
      }
    },
    fixScreenOverflow() {
      if (!this.$refs.popoverContent || !this.$refs.popover) {
        return;
      }
      let customStyle = {};
      const { height, bottom, right } = this.$refs.popoverContent.getBoundingClientRect();
      const triggerRect = this.$refs.popover.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const isBottomOverflowing = bottom > viewportHeight;
      const isRightSideOverflowing = right > viewportWidth;

      if (isBottomOverflowing) {
        const fitsAbove = triggerRect.top >= height;
        if (fitsAbove) {
          customStyle = {
            ...customStyle,
            top: 'auto',
            bottom: '100%',
          };
        } else {
          const heightDifference = bottom - viewportHeight;
          const maxHeight = height - heightDifference;
          customStyle = {
            ...customStyle,
            'max-height': `${maxHeight}px`,
            'overflow-y': 'scroll',
          };
        }
      }

      if (isRightSideOverflowing) {
        const widthDifference = right - viewportWidth;
        customStyle = {
          ...customStyle,
          left: `-${widthDifference}px`,
        };
      }
      this.overflowStyle = customStyle;
    },
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.clickOutsideHandler);
    window.removeEventListener('scroll', this.scrollCloseHandler);
  },
};
</script>

<style lang="scss" scoped>
@use "@sass/design-system/variables" as ds-variables;
@use "@sass/base/variables";

.ds-popover {
  display: inline-block;
  position: relative;

  :global(.fade-enter-active), :global(.fade-leave-active) {
    transition: opacity .3s;
  }

  :global(.fade-enter), :global(.fade-leave-to) {
    opacity: 0;
  }
}

.ds-popover-content {
  display: block;
  position: absolute;
  padding: 16px;
  border: 1px solid #eeeeee;
  border-radius: 8px;
  background-color: ds-variables.$white;
  z-index: 1000;

  &--bottom {
    margin-top: 1px;
    top: 100%;
  }

  &--top {
    margin-bottom: 1px;
    bottom: 100%;
  }

  &--left {
    margin-right: 1px;
    right: 100%;
    transform: translate3d(0, 0, 0);
    top: 0;
    bottom: auto;
  }

  &--top-right {
    margin-bottom: 1px;
    bottom: 100%;
    margin-top: 1px;
    left: auto;
    right: 0;
    transform: translate3d(0, 0, 0);
  }

  &--bottom-left {
    margin-top: 1px;
    left: 0;
    transform: translate3d(0, 0, 0);
    top: 100%;
  }

  &--bottom-right {
    margin-top: 1px;
    left: auto;
    right: 0;
    transform: translate3d(0, 0, 0);
    top: 100%;
  }
}

.ds-popover-content--no-padding {
  padding: 8px 0;
}

@media only screen and (max-width: variables.$screen-xs-max) { // 767 px
  .ds-popover-content--desktop {
    display: none;
  }
}

</style>
